/**
 * ChangePasswordDialog — lets the signed-in user set/change their own password.
 *
 * Calls authApi.setMyPassword → POST /auth/me/password { new_password,
 * current_password? } (verified against the LOS OpenAPI: SelfPasswordRequest).
 * `current_password` is optional because OTP-only users may never have set one;
 * we send it only when the field is filled. New-password rules mirror the reset
 * flow (ResetPasswordScreen): ≥12 chars and confirmation must match.
 */
import { useState } from "react";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useSnackbar } from "notistack";

import { authApi } from "../api/authApi";

const MIN_LENGTH = 12;

// Shared show/hide adornment for password fields.
const revealAdornment = (shown, onToggle) => ({
  endAdornment: (
    <InputAdornment position="end">
      <IconButton
        onClick={onToggle}
        edge="end"
        tabIndex={-1}
        size="small"
        aria-label={shown ? "Hide password" : "Show password"}
      >
        {shown ? (
          <VisibilityOff fontSize="small" />
        ) : (
          <Visibility fontSize="small" />
        )}
      </IconButton>
    </InputAdornment>
  ),
});

const ChangePasswordDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const toggleShow = () => setShow((v) => !v);
  const fieldType = show ? "text" : "password";

  const reset = () => {
    setCurrentPassword("");
    setPassword("");
    setConfirm("");
    setError(null);
    setBusy(false);
    setShow(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await authApi.setMyPassword(password, currentPassword || undefined);
      enqueueSnackbar("Password updated.", { variant: "success" });
      reset();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to update password.",
      );
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Change password</DialogTitle>
      <Stack component="form" onSubmit={submit}>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <TextField
              label="Current password (optional)"
              type={fieldType}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              helperText="Leave blank if you've never set one (e.g. you sign in with an email code)."
              InputProps={revealAdornment(show, toggleShow)}
            />
            <TextField
              label="New password"
              type={fieldType}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              helperText={`At least ${MIN_LENGTH} characters.`}
              InputProps={revealAdornment(show, toggleShow)}
            />
            <TextField
              label="Confirm new password"
              type={fieldType}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              fullWidth
              InputProps={revealAdornment(show, toggleShow)}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={busy} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? "Saving…" : "Update password"}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};

export default ChangePasswordDialog;
